import {
  REINE_ANZEIGE_TYPEN,
  type Abschnitt,
  type Feld,
  type FormSchema,
} from './schemas/form-schema.js';
import type { ProtokollData } from './schemas/protokoll.js';
import {
  fotoReferenzSchema,
  istFotoReferenz,
  istMangel,
  mangelSchema,
  sammleFotoReferenzen,
  type FotoReferenz,
  type Mangel,
} from './schemas/protokoll-daten.js';
import {
  formatiereDatum,
  formatiereUhrzeit,
  formatiereWaehrung,
  formatiereZahl,
} from './format.js';
import { istSichtbar } from './protokoll-validierung.js';
import { hashPraefix } from './hash.js';
import type { BildAufloeser, LayoutKontext } from './handlebars-helpers.js';

/**
 * Bereitet Protokolldaten für das Layout auf.
 *
 * Das Layout soll nicht wissen müssen, wie `formSchema` aufgebaut ist — es
 * bekommt fertige Abschnitte mit Beschriftung und Anzeigetext. Dieselbe
 * Funktion läuft in der Browser-Vorschau und in der PDF-Lambda, damit beide
 * dasselbe Ergebnis liefern (CLAUDE.md §3.5).
 */

export interface LayoutEintrag {
  id: string;
  label: string;
  /** Fertig formatierter Anzeigewert, ggf. mit einfachem HTML. */
  anzeige: string;
}

export interface LayoutZeile {
  titel: string;
  eintraege: LayoutEintrag[];
}

export interface LayoutAbschnitt {
  id: string;
  titel: string;
  typ: 'standard' | 'wiederholbar' | 'tabelle';
  eintraege?: LayoutEintrag[];
  zeilen?: Array<LayoutZeile | { zellen: string[] }>;
  spalten?: string[];
}

export interface LayoutMangel {
  nummer: number;
  ort: string;
  beschreibung: string;
  kategorie: string;
  schweregrad: string;
  zuordnung: string;
  kostenschaetzung: string;
  fotoNummern: string;
  status: string;
}

const escape = (wert: string): string =>
  wert.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const zeilenumbrueche = (wert: string): string => escape(wert).replace(/\r?\n/g, '<br />');

const feldLabel = (feld: Feld): string => ('label' in feld && feld.label ? feld.label : feld.id);

/**
 * Nummerierung des Fotoanhangs: Foto-ID → laufende Nummer.
 *
 * Die Reihenfolge folgt dem **Formularschema**, nicht der Schlüsselreihenfolge
 * der Daten. Nur so ergibt derselbe Datenstand immer dieselbe Nummerierung —
 * Voraussetzung dafür, dass ein Protokoll byte-identisch reproduzierbar bleibt
 * (CLAUDE.md §3.5).
 */
export const fotoNummerierung = (schema: FormSchema, data: ProtokollData): Map<string, number> => {
  const nummern = new Map<string, number>();
  let laufend = 0;

  const merke = (wert: unknown): void => {
    if (!Array.isArray(wert)) {
      if (istFotoReferenz(wert) && !nummern.has(wert.id)) {
        laufend += 1;
        nummern.set(wert.id, laufend);
      }
      return;
    }
    for (const eintrag of wert) {
      if (istFotoReferenz(eintrag)) {
        if (!nummern.has(eintrag.id)) {
          laufend += 1;
          nummern.set(eintrag.id, laufend);
        }
        continue;
      }
      // Mängel tragen eigene Fotos; sie zählen an der Stelle ihres Mangels.
      if (istMangel(eintrag)) merke(eintrag.fotos);
    }
  };

  const durchlaufeFelder = (felder: readonly Feld[], werte: Record<string, unknown>): void => {
    for (const feld of felder) {
      const wert = werte[feld.id];
      if (feld.typ === 'wiederholbar') {
        const zeilen = Array.isArray(wert) ? wert : [];
        for (const zeile of zeilen) {
          durchlaufeFelder(feld.felder, (zeile ?? {}) as Record<string, unknown>);
        }
        continue;
      }
      if (feld.typ === 'fotos' || feld.typ === 'maengelblock') {
        merke(wert);
        continue;
      }
      if (feld.typ === 'zaehlerstand') {
        merke((wert as { fotos?: unknown } | undefined)?.fotos);
      }
    }
  };

  for (const abschnitt of schema.abschnitte) {
    const typ = abschnitt.typ ?? 'standard';
    const rohwert = data[abschnitt.id];

    if (typ === 'wiederholbar') {
      const zeilen = Array.isArray(rohwert) ? rohwert : [];
      for (const zeile of zeilen) {
        durchlaufeFelder(abschnitt.felder ?? [], (zeile ?? {}) as Record<string, unknown>);
      }
      continue;
    }
    if (typ === 'standard') {
      durchlaufeFelder(abschnitt.felder ?? [], (rohwert ?? {}) as Record<string, unknown>);
    }
  }

  return nummern;
};

const fotoListe = (wert: unknown, nummern: Map<string, number>): string => {
  if (!Array.isArray(wert) || wert.length === 0) return '—';
  const teile = wert
    .filter(istFotoReferenz)
    .map((eintrag) => fotoReferenzSchema.parse(eintrag))
    .map((referenz: FotoReferenz) => {
      const nummer = nummern.get(referenz.id);
      return nummer ? `Foto ${nummer}` : 'Foto';
    });
  return teile.length > 0 ? escape(teile.join(', ')) : '—';
};

const maengelText = (wert: unknown, nummern: Map<string, number>): string => {
  if (!Array.isArray(wert) || wert.length === 0) return 'keine';
  const zeilen = wert
    .filter(istMangel)
    .map((eintrag) => mangelSchema.parse(eintrag))
    .map((mangel: Mangel, index) => {
      const verweise = mangel.fotos
        .map((foto) => nummern.get(foto.id))
        .filter((nummer): nummer is number => nummer !== undefined)
        .map((nummer) => `Foto ${nummer}`)
        .join(', ');
      const zusatz = [mangel.kategorie, mangel.schweregrad, verweise].filter(Boolean).join(' · ');
      return `${index + 1}. ${escape(mangel.beschreibung)}${zusatz ? ` <span class="mangel-zusatz">(${escape(zusatz)})</span>` : ''}`;
    });
  return zeilen.join('<br />');
};

const schluesselText = (wert: unknown): string => {
  if (!Array.isArray(wert) || wert.length === 0) return 'keine';
  return wert
    .map((zeile) => {
      const eintrag = zeile as {
        bezeichnung?: string;
        anzahl?: number;
        uebergeben?: number;
        nummer?: string;
      };
      const teile = [
        `${escape(eintrag.bezeichnung ?? '')}: ${eintrag.uebergeben ?? 0} von ${eintrag.anzahl ?? 0}`,
      ];
      if (eintrag.nummer) teile.push(`Nr. ${escape(eintrag.nummer)}`);
      return teile.join(', ');
    })
    .join('<br />');
};

/** Formatiert einen einzelnen Feldwert für die Anzeige im PDF. */
export const anzeigeWert = (feld: Feld, wert: unknown, nummern: Map<string, number>): string => {
  if (wert === null || wert === undefined || wert === '') return '—';

  switch (feld.typ) {
    case 'datum':
      return escape(formatiereDatum(String(wert)));
    case 'uhrzeit':
      return escape(typeof wert === 'string' ? wert : formatiereUhrzeit(String(wert)));
    case 'zahl': {
      const zahl = typeof wert === 'number' ? wert : Number(wert);
      const text = formatiereZahl(zahl, feld.dezimalstellen ?? 2);
      return escape(feld.einheit ? `${text} ${feld.einheit}` : text);
    }
    case 'waehrung':
      return escape(formatiereWaehrung(typeof wert === 'number' ? wert : Number(wert)));
    case 'jaNein':
      return wert === true ? 'Ja' : wert === false ? 'Nein' : '—';
    case 'mehrfachauswahl':
      return Array.isArray(wert) && wert.length > 0 ? escape(wert.join(', ')) : '—';
    case 'mehrzeilig':
      return zeilenumbrueche(String(wert));
    case 'fotos':
      return fotoListe(wert, nummern);
    case 'maengelblock':
      return maengelText(wert, nummern);
    case 'schluesseltabelle':
      return schluesselText(wert);
    case 'zaehlerstand': {
      const stand = wert as { art?: string; nummer?: string; stand?: number | null };
      if (stand.stand === null || stand.stand === undefined) return '—';
      const teile = [
        escape(stand.art ?? ''),
        formatiereZahl(stand.stand, feld.dezimalstellen ?? 2),
      ];
      if (stand.nummer) teile.push(`Nr. ${escape(stand.nummer)}`);
      return teile.filter(Boolean).join(' · ');
    }
    default:
      return escape(String(wert));
  }
};

const eintraegeFuer = (
  felder: readonly Feld[],
  werte: Record<string, unknown>,
  nummern: Map<string, number>,
): LayoutEintrag[] =>
  felder
    .filter((feld) => !REINE_ANZEIGE_TYPEN.includes(feld.typ) && feld.typ !== 'signaturblock')
    .filter((feld) => istSichtbar(feld.sichtbarWenn, werte))
    .filter((feld) => feld.typ !== 'wiederholbar')
    .map((feld) => ({
      id: feld.id,
      label: feldLabel(feld),
      anzeige: anzeigeWert(feld, werte[feld.id], nummern),
    }));

const zeilenTitel = (
  abschnitt: Abschnitt,
  zeile: Record<string, unknown>,
  index: number,
): string => {
  if (abschnitt.titelFeld && zeile[abschnitt.titelFeld]) return String(zeile[abschnitt.titelFeld]);
  const ersterText = (abschnitt.felder ?? []).find((feld) => feld.typ === 'text');
  if (ersterText && zeile[ersterText.id]) return String(zeile[ersterText.id]);
  return `${abschnitt.titel} ${index + 1}`;
};

/** Baut die Abschnittsliste, die das Standard-Layout erwartet. */
export const baueLayoutAbschnitte = (
  schema: FormSchema,
  data: ProtokollData,
  nummern: Map<string, number> = fotoNummerierung(schema, data),
): LayoutAbschnitt[] => {
  const ergebnis: LayoutAbschnitt[] = [];

  for (const abschnitt of schema.abschnitte) {
    const typ = abschnitt.typ ?? 'standard';
    if (typ === 'signaturblock') continue;
    if (!istSichtbar(abschnitt.sichtbarWenn, data as Record<string, unknown>)) continue;

    const rohwert = data[abschnitt.id];

    if (typ === 'schluesseltabelle') {
      const zeilen = Array.isArray(rohwert) ? rohwert : [];
      ergebnis.push({
        id: abschnitt.id,
        titel: abschnitt.titel,
        typ: 'tabelle',
        spalten: ['Bezeichnung', 'Nummer', 'Bestand', 'Übergeben', 'Bemerkung'],
        zeilen: zeilen.map((zeile) => {
          const eintrag = zeile as Record<string, unknown>;
          return {
            zellen: [
              escape(String(eintrag.bezeichnung ?? '')),
              escape(String(eintrag.nummer ?? '—')),
              String(eintrag.anzahl ?? 0),
              String(eintrag.uebergeben ?? 0),
              escape(String(eintrag.bemerkung ?? '')),
            ],
          };
        }),
      });
      continue;
    }

    if (typ === 'wiederholbar') {
      const zeilen = Array.isArray(rohwert) ? rohwert : [];
      ergebnis.push({
        id: abschnitt.id,
        titel: abschnitt.titel,
        typ: 'wiederholbar',
        zeilen: zeilen.map((zeile, index) => {
          const werte = (zeile ?? {}) as Record<string, unknown>;
          return {
            titel: zeilenTitel(abschnitt, werte, index),
            eintraege: eintraegeFuer(abschnitt.felder ?? [], werte, nummern),
          };
        }),
      });
      continue;
    }

    const werte = (rohwert ?? {}) as Record<string, unknown>;
    ergebnis.push({
      id: abschnitt.id,
      titel: abschnitt.titel,
      typ: 'standard',
      eintraege: eintraegeFuer(abschnitt.felder ?? [], werte, nummern),
    });
  }

  return ergebnis;
};

/** Flache Mängelübersicht über alle Abschnitte, mit Ortsangabe. */
export const baueMaengeluebersicht = (
  schema: FormSchema,
  data: ProtokollData,
  nummern: Map<string, number> = fotoNummerierung(schema, data),
): LayoutMangel[] => {
  const ergebnis: LayoutMangel[] = [];
  let laufend = 0;

  const erfasse = (wert: unknown, ort: string): void => {
    if (!Array.isArray(wert)) return;
    for (const eintrag of wert) {
      const geprueft = mangelSchema.safeParse(eintrag);
      if (!geprueft.success) continue;
      const mangel = geprueft.data;
      laufend += 1;
      ergebnis.push({
        nummer: laufend,
        ort,
        beschreibung: mangel.beschreibung,
        kategorie: mangel.kategorie ?? '',
        schweregrad: mangel.schweregrad ?? '',
        zuordnung: mangel.zuordnung ?? '',
        kostenschaetzung:
          mangel.kostenschaetzung === undefined ? '' : formatiereWaehrung(mangel.kostenschaetzung),
        fotoNummern: mangel.fotos
          .map((foto) => nummern.get(foto.id))
          .filter((nummer): nummer is number => nummer !== undefined)
          .map((nummer) => String(nummer))
          .join(', '),
        status: mangel.status ?? '',
      });
    }
  };

  for (const abschnitt of schema.abschnitte) {
    const typ = abschnitt.typ ?? 'standard';
    const rohwert = data[abschnitt.id];

    if (typ === 'wiederholbar' && Array.isArray(rohwert)) {
      rohwert.forEach((zeile, index) => {
        const werte = (zeile ?? {}) as Record<string, unknown>;
        const ort = zeilenTitel(abschnitt, werte, index);
        for (const feld of abschnitt.felder ?? []) {
          if (feld.typ === 'maengelblock') erfasse(werte[feld.id], ort);
        }
      });
      continue;
    }

    const werte = (rohwert ?? {}) as Record<string, unknown>;
    for (const feld of abschnitt.felder ?? []) {
      if (feld.typ === 'maengelblock') erfasse(werte[feld.id], abschnitt.titel);
    }
  }

  return ergebnis;
};

// ---------------------------------------------------------------------------
// Vollständiger Layout-Kontext
// ---------------------------------------------------------------------------

export interface LayoutKontextEingabe {
  formSchema: FormSchema;
  data: ProtokollData;
  branding: Record<string, unknown>;
  objekt?: Record<string, unknown> | null;
  /** Übergebene Einheit. Fehlt bei Protokollen ohne Einheitenbezug. */
  einheit?: Record<string, unknown> | null;
  protokollNr: string;
  art: string;
  status: string;
  uebergabeDatum: string | null;
  templateVersion: number;
  dataHash: string;
  erstelltAm: string;
  verifikationsUrl?: string;
  beteiligte?: Array<Record<string, unknown>>;
  signaturen?: Array<Record<string, unknown>>;
  bilder?: BildAufloeser;
}

/**
 * Baut den vollständigen Kontext, den ein Layout erwartet.
 *
 * Diese Funktion ist der Kern der Rendering-Parität (CLAUDE.md §3.5): Vorschau
 * und PDF-Lambda bauen den Kontext **nicht** mehr jede für sich. Vorher fehlten
 * der Lambda `abschnitte` und `meta.maengel` — das PDF zeigte deshalb Kopf,
 * Fuß und Unterschriften, aber keine einzige erfasste Angabe.
 *
 * Die Fotonummern stammen ausschließlich aus `fotoNummerierung`, also aus der
 * Reihenfolge des Formularschemas. Nur so verweist „Foto 3" im Feldtext auf
 * dasselbe Bild wie „Foto 3" im Anhang.
 */
export const baueLayoutKontext = (eingabe: LayoutKontextEingabe): LayoutKontext => {
  const nummern = fotoNummerierung(eingabe.formSchema, eingabe.data);
  const beschriftungen = new Map(
    sammleFotoReferenzen(eingabe.data).map((foto) => [foto.id, foto.beschriftung]),
  );

  /*
   * Fotos, die das Schema nicht mehr erreicht (etwa weil ein Feld später
   * entfernt wurde), hängen hinten an. Ein Bild darf nie stillschweigend aus
   * dem Anhang verschwinden — es ist Bestandteil des signierten Datenstands.
   */
  const nachtrag = [...beschriftungen.keys()].filter((id) => !nummern.has(id));
  let laufend = nummern.size;
  for (const id of nachtrag) {
    laufend += 1;
    nummern.set(id, laufend);
  }

  const fotoAnhang = [...nummern.entries()]
    .sort((links, rechts) => links[1] - rechts[1])
    .map(([id, nummer]) => {
      const beschriftung = beschriftungen.get(id);
      return beschriftung ? { nummer, id, beschriftung } : { nummer, id };
    });

  return {
    data: eingabe.data,
    objekt: eingabe.objekt ?? null,
    einheit: eingabe.einheit ?? null,
    branding: eingabe.branding,
    abschnitte: baueLayoutAbschnitte(eingabe.formSchema, eingabe.data, nummern),
    meta: {
      protokollNr: eingabe.protokollNr,
      art: eingabe.art,
      status: eingabe.status,
      uebergabeDatum: eingabe.uebergabeDatum,
      templateVersion: eingabe.templateVersion,
      dataHash: eingabe.dataHash,
      hashPraefix: hashPraefix(eingabe.dataHash),
      erstelltAm: eingabe.erstelltAm,
      ...(eingabe.verifikationsUrl ? { verifikationsUrl: eingabe.verifikationsUrl } : {}),
      beteiligte: eingabe.beteiligte ?? [],
      signaturen: eingabe.signaturen ?? [],
      fotoAnhang,
      maengel: baueMaengeluebersicht(eingabe.formSchema, eingabe.data, nummern),
    },
    bilder: eingabe.bilder ?? {},
  };
};
